// The meeting's Slack tab (ADR 0009, amended by ADR 0010; owner ruling 20260926-002133, R1–R5, R7, R8):
// whether the tab is there at all, its count, which state it shows — the server's `state` decides, the
// client never infers one from an empty payload (06-modules §5.7) — the banners above it, how findings
// are grouped and ordered, how a result says the way Slack was read (from the check's counts and flags,
// never a judge's words), what a citation shows and where it links, and the words of the "Look in
// Slack" dialog. Pure; unit-tested under node.

import type {
  MeResponse,
  MeetingDetail,
  MeetingSlackContextDigest,
  SlackCheckStepsDto,
  SlackCitationDto,
  SlackContextAnalysisDto,
  SlackContextPreviewResponse,
  SlackContextResponse,
  SlackFindingDto,
  SlackNothingToReadDto,
  SlackPriorReviewDto,
  SlackPriorReviewSubjectDto,
  TodoDto,
} from '../../../shared/api-types.js';
import {
  GLOBAL_CAPABILITY_LABELS,
  SLACK_CONTEXT_PHASE_LABELS,
  SLACK_FINDING_KIND_LABELS,
  SLACK_FINDING_KIND_NAMES,
  SLACK_OPPORTUNITY_TYPE_LABELS,
  SLACK_PARTIAL_REASON_LABELS,
  SLACK_PRIOR_VERDICT_LABELS,
  type AiProvider,
  type SlackConversationKind,
  type SlackFindingImportance,
  type SlackFindingKind,
  type SlackPriorVerdict,
} from '../../../shared/enums.js';
import { SLACK_LOOKBACK_DAYS, SLACK_WINDOW_DAYS_AFTER, SLACK_WINDOW_DAYS_BEFORE } from '../../../shared/slack.js';
import { PATHS } from '../../paths.js';
import { formatCount, formatDate, formatDateTime, formatList, pluralise } from '../../ui/format.js';
import { ONLY_YOU_BUT_CLIMATE } from './climate.js';
import { directMessageWords, SLACK_CHECK_SENDS, slackCheckSteps, slackScopePhrase } from './slack-connection.js';
import { meetingTodoSearch, type TodoDraft } from './todos.js';

export type SlackCapability = SlackContextResponse['missingCapabilities'][number];

// --- Whether the tab is there, and its count --------------------------------------------------------

/** The Slack analysis floor as the page knows it (R1): Slack switched on, and an AI provider to compare with. */
export function slackAnalysisFloorMet(integrations: Pick<MeResponse['integrations'], 'slack' | 'ai'>): boolean {
  return integrations.slack !== 'off' && integrations.ai !== 'off';
}

/**
 * Whether a meeting's page shows its Slack tab (owner ruling D8; design-deviations.md §2). Not while the
 * Slack analysis floor is unmet — no Slack check can exist in such an installation — unless the meeting
 * still holds a completed check from before, which stays readable until it is deleted (D10).
 */
export function showsSlackTab(integrations: Pick<MeResponse['integrations'], 'slack' | 'ai'>, digest: MeetingSlackContextDigest | null): boolean {
  return slackAnalysisFloorMet(integrations) || (digest !== null && digest.completedAt !== null);
}

function totalFindings(counts: SlackContextAnalysisDto['counts']): number | null {
  return counts === null ? null : Object.values(counts).reduce((sum, count) => sum + count, 0);
}

/**
 * The findings of the check held for the meeting, when they can be read; null before a check has
 * completed, and when this installation can no longer open what it found. Why the counts: while a newer
 * check runs, a stored result the key cannot open arrives with no findings — which must not read as a
 * check that found nothing.
 */
export function heldSlackFindings(response: SlackContextResponse): SlackFindingDto[] | null {
  if (response.analysis === null || response.state === 'unreadable') return null;
  if (response.findings.length > 0) return response.findings;
  return totalFindings(response.analysis.counts) === 0 ? [] : null;
}

/**
 * The tab's count: the findings not dismissed. Undefined while it is not known — never 0 before a check
 * has found nothing (06-modules §5.3 R3). The tab's own answer, once loaded, is fresher than the page's.
 */
export function slackTabCount(digest: MeetingSlackContextDigest | null, response: SlackContextResponse | null): number | undefined {
  if (response) {
    const findings = heldSlackFindings(response);
    return findings === null ? undefined : findings.filter((finding) => !finding.dismissed).length;
  }
  return digest?.visibleCount ?? undefined;
}

// --- Which state, and what stands above it ------------------------------------------------------------

export type SlackTabState =
  | 'not-configured'
  | 'denied'
  | 'not-connected'
  | 'no-sources'
  | 'needs-summary'
  | 'never'
  | 'pending'
  | 'running'
  | 'failed'
  | 'unreadable'
  | 'ready';

export interface SlackTabNotice {
  /** Stable key: which condition this is. */
  id: 'connection-error' | 'access-withdrawn' | 'checks-off';
  tone: 'info' | 'warning';
  title: string;
  text: string;
  /** Offer the way to the Slack section of Connections. */
  toConnections: boolean;
}

export interface SlackRequestOffer {
  /** The control's words: it opens the "Look in Slack" dialog, where the person chooses what is read. */
  label: string;
  /** Why it cannot be used now; null when it can. The reason is shown in its place, never a bare disabled button. */
  blockedReason: string | null;
}

export interface SlackContextView {
  state: SlackTabState;
  /** Banners above the tab's content, most urgent first (03-components §6.2). */
  notices: SlackTabNotice[];
  /** The check held, readable: the list when ready, and the previous result under a newer check. */
  findings: SlackFindingDto[] | null;
  /** "Look in Slack…" where this state offers it. */
  request: SlackRequestOffer | null;
}

const STATES: Readonly<Record<Exclude<SlackContextResponse['state'], 'not_permitted'>, SlackTabState>> = {
  not_connected: 'not-connected',
  no_sources: 'no-sources',
  needs_summary: 'needs-summary',
  never: 'never',
  queued: 'pending',
  running: 'running',
  failed: 'failed',
  unreadable: 'unreadable',
  ready: 'ready',
};

const REQUEST_LABELS: Partial<Record<SlackTabState, string>> = {
  never: 'Look in Slack…',
  failed: 'Try again…',
  unreadable: 'Look in Slack again…',
  ready: 'Look in Slack again…',
};

/** "Slack analysis", "Slack connect and Slack analysis". */
function capabilityNames(missing: readonly SlackCapability[]): string {
  // A list the locale joins (05-content §8; Phase 1c D30), never " and " by hand.
  return formatList(missing.map((capability) => GLOBAL_CAPABILITY_LABELS[capability]));
}

/** What the denied state says (R1): which grant is missing, and how to get it. */
export function slackDeniedText(missing: readonly SlackCapability[]): { title: string; description: string } {
  const connect = missing.includes('slack_connect');
  const analysis = missing.includes('slack_analysis');
  if (connect && analysis) {
    return {
      title: 'Your account cannot check meetings against Slack',
      description: `Checking a meeting against Slack needs ${capabilityNames(missing)}, and your groups include neither. Ask an administrator to add you to a group with both.`,
    };
  }
  if (connect) {
    return {
      title: `Slack checks also need ${GLOBAL_CAPABILITY_LABELS.slack_connect}`,
      description: `Calis reads Slack as you, and your groups don’t include ${GLOBAL_CAPABILITY_LABELS.slack_connect}. Ask an administrator to add you to a group with it.`,
    };
  }
  return {
    title: `Slack checks also need ${GLOBAL_CAPABILITY_LABELS.slack_analysis}`,
    description: `Your groups don’t include ${GLOBAL_CAPABILITY_LABELS.slack_analysis}, so Calis can’t check your meetings. You can still connect Slack and choose the channels Calis won’t read; ask an administrator to add you to a group that has it.`,
  };
}

function blockedReason(response: SlackContextResponse, floorMet: boolean): string | null {
  if (response.canRequest) return null;
  if (!floorMet) return 'Slack checks are switched off in this installation.';
  if (response.missingCapabilities.length > 0) return `Looking in Slack needs ${capabilityNames(response.missingCapabilities)}, which your groups don’t include.`;
  if (response.connectionStatus === 'error') return 'Connect Slack again from Connections to look in Slack.';
  if (response.connectionStatus === null || response.connectionStatus === 'revoked') return 'Connect Slack from Connections to look in Slack.';
  if (response.runsLeftToday <= 0) return slackDailyLimitText(response.runsPerDay);
  // What remains is decided by the server alone: a summary, something to read, and a known time.
  return 'Calis can look in Slack for this meeting once it has a summary, it may read at least one of your channels or your direct messages and group chats (Connections), and the meeting’s time is known.';
}

/** "You have used today’s 50 Slack checks…" — the daily limit, in the words the dialog uses too. */
export function slackDailyLimitText(runsPerDay: number): string {
  return `You have used today’s ${formatCount(runsPerDay)} Slack checks. Look in Slack again tomorrow.`;
}

/**
 * Which state the tab shows for a GET /api/meetings/:id/slack-context answer. `floorMet` is the page's
 * knowledge of the Slack analysis floor (/api/me), which tells a switched-off installation from a
 * missing grant — both arrive as `not_permitted`, and they are different answers (app-baseline 05-client
 * §5.4). What was found stays readable after a grant or the floor goes (D10), under a banner that says
 * Calis will not look again.
 */
export function slackContextView(response: SlackContextResponse, options: { floorMet: boolean }): SlackContextView {
  const { floorMet } = options;
  if (response.state === 'not_permitted') {
    const denied = floorMet && response.missingCapabilities.length > 0;
    return { state: denied ? 'denied' : 'not-configured', notices: [], findings: null, request: null };
  }
  const state = STATES[response.state];
  const findings = heldSlackFindings(response);
  const notices: SlackTabNotice[] = [];

  if (response.connectionStatus === 'error') {
    notices.push({
      id: 'connection-error',
      tone: 'warning',
      title: 'Your Slack connection needs attention',
      text: `Calis cannot look in Slack until you connect Slack again from Connections.${findings !== null ? ' What it found before is below.' : ''}`,
      toConnections: true,
    });
  }
  const withdrawn = !floorMet || response.missingCapabilities.length > 0;
  if (withdrawn && (findings !== null || response.analysis !== null)) {
    const kept = `What Calis found before stays readable until it is deleted, ${pluralise(response.retentionDays, { one: '# day', other: '# days' })} after the check.`;
    notices.push(
      floorMet
        ? {
            id: 'access-withdrawn',
            tone: 'warning',
            title: 'Calis will not look in Slack again for you',
            text: `Your access no longer includes ${capabilityNames(response.missingCapabilities)}. ${kept} Ask an administrator if you need it restored.`,
            toConnections: false,
          }
        : { id: 'checks-off', tone: 'info', title: 'Slack checks are switched off in this installation', text: kept, toConnections: false },
    );
  }

  const label = REQUEST_LABELS[state];
  return { state, notices, findings, request: label ? { label, blockedReason: blockedReason(response, floorMet) } : null };
}

/** While a check is waiting or running: the state word (05-content §6) and what it is doing. */
export function slackProgress(response: Pick<SlackContextResponse, 'state' | 'phase'>): { word: 'Pending' | 'Running'; text: string } | null {
  if (response.state === 'queued') return { word: 'Pending', text: 'Waiting to look in Slack' };
  if (response.state === 'running') return { word: 'Running', text: SLACK_CONTEXT_PHASE_LABELS[response.phase ?? 'reading'] };
  return null;
}

// --- The result ---------------------------------------------------------------------------------------

/** Group order on the tab: what contradicts the meeting first, then what adds to it, then what to do. */
export const SLACK_FINDING_GROUP_ORDER: readonly SlackFindingKind[] = Object.freeze(['inconsistency', 'context', 'opportunity']);

const IMPORTANCE_RANK: Readonly<Record<SlackFindingImportance, number>> = { high: 0, medium: 1, low: 2 };

export interface SlackFindingGroup {
  kind: SlackFindingKind;
  heading: string;
  /** Not dismissed, most important first, then in the order the check gave them. */
  visible: SlackFindingDto[];
  dismissed: SlackFindingDto[];
}

export function groupSlackFindings(findings: readonly SlackFindingDto[]): SlackFindingGroup[] {
  return SLACK_FINDING_GROUP_ORDER.map((kind) => {
    const ofKind = findings
      .map((finding, order) => ({ finding, order }))
      .filter(({ finding }) => finding.kind === kind)
      .sort((a, b) => IMPORTANCE_RANK[a.finding.importance] - IMPORTANCE_RANK[b.finding.importance] || a.order - b.order)
      .map(({ finding }) => finding);
    return { kind, heading: SLACK_FINDING_KIND_LABELS[kind], visible: ofKind.filter((finding) => !finding.dismissed), dismissed: ofKind.filter((finding) => finding.dismissed) };
  });
}

/** What a partial check did not read, for the banner above its findings; null when it read everything. */
export function slackPartialText(analysis: Pick<SlackContextAnalysisDto, 'partialReason' | 'sourcesUnreadable'>): string | null {
  if (analysis.partialReason === null) return null;
  if (analysis.partialReason === 'source_unreadable' && analysis.sourcesUnreadable !== null && analysis.sourcesUnreadable > 0) {
    return `${pluralise(analysis.sourcesUnreadable, { one: '# conversation', other: '# conversations' })} could not be read — you may have left a channel, or it was archived.`;
  }
  return SLACK_PARTIAL_REASON_LABELS[analysis.partialReason];
}

/**
 * `13 – 23 Sep 2026`, `28 Sep – 5 Oct 2026`, `28 Dec 2026 – 4 Jan 2027`, or one date when both fall on
 * the same day, in the reader's zone (04-data §4: unambiguous dates).
 */
export function formatDateRange(start: string, end: string, timeZone: string): string {
  const from = formatDate(start, { timeZone });
  const to = formatDate(end, { timeZone });
  const a = from.split(' ');
  const b = to.split(' ');
  if (a.length !== 3 || b.length !== 3) return `${from} – ${to}`;
  if (from === to) return from;
  if (a[2] !== b[2]) return `${from} – ${to}`;
  if (a[1] !== b[1]) return `${a[0]} ${a[1]} – ${to}`;
  return `${a[0]} – ${to}`;
}

export interface SlackProvenance {
  /** "From 14 of 39 messages in 6 conversations, 12 – 16 Sep 2026". */
  read: string;
  /** "Searched Slack twice, then read 140 conversations" (owner ruling R8 §7); null for a check from before these were recorded. */
  how: string | null;
  /** "3 of 3 attendees found in Slack"; null when the meeting had none to look for. */
  attendees: string | null;
  /** Who compared them — "the demo analyser", "Anthropic Claude (…)" — or null when nothing needed comparing. */
  by: string | null;
}

/**
 * How a check read Slack, in words derived from its recorded counts and flags alone (owner ruling R8
 * §7): the rounds of searches, whether the last judge found them enough, and how many conversations
 * were read one by one after them. Never a judge's own words — those are neither stored nor shown.
 */
/** How often a check searched — at most twice (ADR 0010) — in words; each a complete string, never a plural made by hand (05-content §8). */
const SEARCH_TIMES: Readonly<Record<number, string>> = { 1: 'once', 2: 'twice' };

export function slackStepsText(steps: Pick<SlackCheckStepsDto, 'searchRounds' | 'searchUnavailable' | 'judgeVerdicts' | 'conversationsReadInFallback'>): string {
  const read = steps.conversationsReadInFallback;
  const conversations = read === 0 ? 'no conversations' : pluralise(read, { one: '# conversation', other: '# conversations' });
  if (steps.searchRounds <= 0) {
    return steps.searchUnavailable
      ? `Slack’s search was unavailable, so Calis read ${conversations}`
      : `No words in the meeting’s record to search Slack for, so Calis read ${conversations}`;
  }
  const times = SEARCH_TIMES[steps.searchRounds] ?? pluralise(steps.searchRounds, { one: '# time', other: '# times' });
  const last = steps.judgeVerdicts[steps.judgeVerdicts.length - 1];
  // Why three whole templates (05-content §8; the Phase 1c audit's D30): " (then search was unavailable)" was spliced in
  // by condition.
  if (last === true && read === 0) return `Searched Slack ${times}; what it found was enough`;
  if (steps.searchUnavailable) return `Searched Slack ${times} (then search was unavailable), then read ${conversations}`;
  return `Searched Slack ${times}, then read ${conversations}`;
}

/** The line above a result, modelled on the summary's provenance: what was read, how, and by whom it was compared. */
export function slackProvenance(analysis: SlackContextAnalysisDto, timeZone: string): SlackProvenance {
  const messages = (count: number) => pluralise(count, { one: '# message', other: '# messages' });
  const sent = analysis.messagesSent;
  const considered = analysis.messagesConsidered;
  let lead: string;
  // Why no fraction when the two are equal: "From 8 of 8 messages" says nothing "From 8 messages" does not.
  if (sent !== null && sent > 0) lead = considered !== null && considered !== sent ? `From ${formatCount(sent)} of ${messages(considered)}` : `From ${messages(sent)}`;
  else lead = considered !== null ? `Read ${messages(considered)}` : 'Read';
  const where = analysis.sourcesRead !== null ? pluralise(analysis.sourcesRead, { one: '# conversation', other: '# conversations' }) : null;
  const when = analysis.window ? formatDateRange(analysis.window.start, analysis.window.end, timeZone) : null;
  // Why four whole templates over the three parts (05-content §8; Phase 1c D30): " in …" and ", dates" were appended by
  // condition with `+=`.
  const read = where !== null && when !== null ? `${lead} in ${where}, ${when}` : where !== null ? `${lead} in ${where}` : when !== null ? `${lead}, ${when}` : lead;
  const attendees =
    analysis.attendeesTotal !== null && analysis.attendeesTotal > 0
      ? `${formatCount(analysis.attendeesMatched ?? 0)} of ${pluralise(analysis.attendeesTotal, { one: '# attendee', other: '# attendees' })} found in Slack`
      : null;
  const by = analysis.isDemo ? 'the demo analyser' : analysis.provider === 'anthropic' ? (analysis.model ? `Anthropic Claude (${analysis.model})` : 'Anthropic Claude') : null;
  return { read, how: analysis.steps ? slackStepsText(analysis.steps) : null, attendees, by };
}

/**
 * Under the empty result's title ("Nothing from Slack added to this meeting"): what the check looked at
 * (05-content §5), so "nothing" is not read as "not looked" — and nothing the title already says.
 */
export function slackEmptyResultText(analysis: SlackContextAnalysisDto, timeZone: string): string {
  const where = analysis.sourcesRead !== null ? pluralise(analysis.sourcesRead, { one: '# conversation', other: '# conversations' }) : 'Slack';
  // Why whole sentences (05-content §8; Phase 1c D30): the dates were spliced in by condition; the coverage note is a
  // sentence of its own, said after.
  const read = analysis.window ? `Calis read ${where}, ${formatDateRange(analysis.window.start, analysis.window.end, timeZone)}.` : `Calis read ${where}.`;
  return analysis.coverageNote ? `${read} ${analysis.coverageNote}` : read;
}

// --- One finding ----------------------------------------------------------------------------------------

/** Citations shown under a finding before the rest go behind a disclosure. */
export const SLACK_CITATIONS_SHOWN = 3;

/** The finding's citations, in the order it cites them; an id the answer does not hold is left out. */
export function slackCitationsOf(finding: Pick<SlackFindingDto, 'citationIds'>, citations: readonly SlackCitationDto[]): SlackCitationDto[] {
  const byId = new Map(citations.map((citation) => [citation.id, citation]));
  return finding.citationIds.map((id) => byId.get(id)).filter((citation): citation is SlackCitationDto => citation !== undefined);
}

export interface SlackMeetingLink {
  label: string;
  /** Where on this page: `?tab=…` and an optional `#…`. Null for an item of an earlier summary, which no longer exists as such. */
  search: string | null;
  hash: string | null;
}

/**
 * Where a finding touches the meeting, as places on this page: the transcript at the moment it was said,
 * and the summary's key points, decisions and to-dos. An item from an earlier summary has no link — the
 * summary it belonged to has been replaced.
 */
export function slackMeetingLinks(finding: Pick<SlackFindingDto, 'meetingRefs' | 'transcriptSegmentIndex'>, options: { earlierSummary: boolean }): SlackMeetingLink[] {
  const links: SlackMeetingLink[] = [];
  const segment = finding.transcriptSegmentIndex;
  if (segment !== null) links.push({ label: `Transcript, segment ${formatCount(segment + 1)}`, search: '?tab=transcript', hash: `#t-${segment}` });
  for (const ref of finding.meetingRefs) {
    const label = ref.type === 'key_point' ? `Key point ${formatCount(ref.index + 1)}` : ref.type === 'decision' ? `Decision ${formatCount(ref.index + 1)}` : 'The to-do';
    const search = options.earlierSummary ? null : ref.type === 'todo' ? meetingTodoSearch(ref.todoId) : '?tab=summary';
    links.push({ label, search, hash: null });
  }
  return links;
}

/**
 * "Add as to-do…" (owner ruling D4; ADR 0007): an opportunity's suggested to-do as a draft for the
 * person's own to-do form — the text as suggested, high priority for a finding of high importance and
 * medium otherwise. The person edits it and adds it themselves; nothing becomes a to-do by itself. Null
 * for any other finding, for an opportunity without a suggestion, and for a finding the person dismissed.
 */
export function slackTodoDraft(finding: Pick<SlackFindingDto, 'kind' | 'suggestedTodo' | 'importance' | 'dismissed'>): TodoDraft | null {
  if (finding.kind !== 'opportunity' || finding.dismissed) return null;
  const text = finding.suggestedTodo?.trim() ?? '';
  if (text === '') return null;
  return { text, priority: finding.importance === 'high' ? 'high' : 'medium' };
}

export interface SlackSide {
  text: string;
  /** When it was said: the meeting's start, or the latest message cited. */
  at: string | null;
  /** The later of the two sides, which the tab marks. */
  later: boolean;
}

/** An inconsistency's two sides, each dated, the later one marked; null for any other finding. */
export function slackInconsistencySides(
  finding: Pick<SlackFindingDto, 'kind' | 'meetingSide' | 'slackSide'>,
  cited: readonly Pick<SlackCitationDto, 'postedAt'>[],
  meetingStartedAt: string | null,
): { meeting: SlackSide; slack: SlackSide } | null {
  if (finding.kind !== 'inconsistency' || finding.meetingSide === null || finding.slackSide === null) return null;
  const slackTimes = cited.map((citation) => Date.parse(citation.postedAt)).filter((time) => !Number.isNaN(time));
  const slackTime = slackTimes.length > 0 ? Math.max(...slackTimes) : Number.NaN;
  const meetingTime = meetingStartedAt ? Date.parse(meetingStartedAt) : Number.NaN;
  const comparable = !Number.isNaN(slackTime) && !Number.isNaN(meetingTime);
  return {
    meeting: { text: finding.meetingSide, at: Number.isNaN(meetingTime) ? null : meetingStartedAt, later: comparable && meetingTime > slackTime },
    slack: { text: finding.slackSide, at: Number.isNaN(slackTime) ? null : new Date(slackTime).toISOString(), later: comparable && slackTime > meetingTime },
  };
}

// --- One citation ---------------------------------------------------------------------------------------

/** Who wrote a cited message; Slack sometimes cannot say. */
export function slackCitationAuthor(citation: Pick<SlackCitationDto, 'authorName'>): string {
  return citation.authorName ?? 'Someone in Slack';
}

/** The excerpt split around the checked quote, which the tab marks; the whole excerpt when there is none. */
export function slackExcerptParts(citation: Pick<SlackCitationDto, 'excerpt' | 'quoteRange'>): { before: string; quote: string | null; after: string } {
  const text = citation.excerpt;
  const range = citation.quoteRange;
  if (range === null) return { before: text, quote: null, after: '' };
  const [start, end] = range;
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end > text.length || start >= end) return { before: text, quote: null, after: '' };
  return { before: text.slice(0, start), quote: text.slice(start, end), after: text.slice(end) };
}

function lowerFirst(label: string): string {
  // "Direct message with …" reads as "direct message with …" mid-sentence; "#ops" and "DM" stay as they are.
  return /^[A-Z][a-z]/u.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;
}

/** "message from Ana Lomidze in #ops, 15 Sep 2026, 17:00 BST": what an "Open in Slack" link opens. */
export function slackCitationDescription(citation: Pick<SlackCitationDto, 'authorName' | 'conversationLabel' | 'postedAt'>, timeZone: string): string {
  return `message from ${slackCitationAuthor(citation)} in ${lowerFirst(citation.conversationLabel)}, ${formatDateTime(citation.postedAt, { timeZone })}`;
}

const PERMALINK_HOST = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.slack\.com$/u;
const PERMALINK_PATH = /^\/archives\/[CDG][A-Z0-9]+\/p\d{16}$/u;

/**
 * The link "Open in Slack" may carry: an https Slack message address and nothing else. The server checks
 * a permalink when it stores it and again when it serves it; the page checks once more before it puts
 * one in an href, so nothing else can ever be opened from a finding.
 */
export function slackPermalinkHref(permalink: string | null): string | null {
  if (!permalink) return null;
  let url: URL;
  try {
    url = new URL(permalink);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '' || url.port !== '' || url.hash !== '') return null;
  if (!PERMALINK_HOST.test(url.hostname) || !PERMALINK_PATH.test(url.pathname)) return null;
  for (const key of url.searchParams.keys()) if (key !== 'thread_ts' && key !== 'cid') return null;
  return url.toString();
}

// --- Deep links -----------------------------------------------------------------------------------------

const FINDING_HASH = /^#s-(f[0-9a-f]{16}(?:-\d{1,3})?)$/u;

/** `s-<finding id>`: the finding's element, and the fragment a link to it carries (`?tab=slack#s-…`). */
export function slackFindingDomId(findingId: string): string {
  return `s-${findingId}`;
}

/** The finding a `#s-…` fragment asks for, or null. */
export function slackFindingTarget(hash: string): string | null {
  return FINDING_HASH.exec(hash)?.[1] ?? null;
}

/**
 * Where a to-do's Slack context lives: its meeting's Slack tab, at the first finding it rests on when one
 * is known (`?tab=slack#s-<id>`) and the tab itself otherwise. The tab says so when the finding is no
 * longer there — a to-do outlives its check (owner ruling A4), the link need not.
 */
export function slackContextHref(meetingId: string, findingIds: readonly string[]): string {
  const [first] = findingIds;
  return `${PATHS.meeting(meetingId)}?tab=slack${first ? `#${slackFindingDomId(first)}` : ''}`;
}

/** `st-<id>`: a matter's or an update's element, and the fragment a link to it carries (`/assistant?tab=slack-today#st-…`). */
export function slackTodayDomId(id: string): string {
  return `st-${id}`;
}

/**
 * Where a daily sweep's item is shown: its card on the Assistant's "Slack today" tab, or the tab itself when no item is
 * known. Why here and not beside the tab's other helpers (lib/assistant.ts, which re-exports it): a sweep's to-do leads
 * there from "Why?", and that rule lives in this module.
 */
export function slackTodayHref(findingId: string | null = null): string {
  return `${PATHS.assistant}?tab=slack-today${findingId ? `#${slackTodayDomId(findingId)}` : ''}`;
}

/**
 * What the reader can open, for the way from a to-do back to Slack: `slackToday` — the Assistant's "Slack today" tab
 * (lib/assistant.ts canOpenSlackToday). Why it matters: the daily sweep reads the day of anyone with Slack access
 * (owner, round A: "Anyone with Slack access"), but its findings sit behind the Assistant; a person without it reads
 * a sweep to-do's reason and is offered no link that would only refuse them.
 */
export interface TodoWhyOptions {
  slackToday?: boolean;
}

/** Whether a to-do carries a "Why?": one the AI made (owner ruling A2), with a reason or a way back to its finding. */
export function todoHasWhy(todo: Pick<TodoDto, 'source' | 'why' | 'meeting' | 'findingIds' | 'fromSweep'>, options: TodoWhyOptions = {}): boolean {
  if (todo.source !== 'slack' && todo.source !== 'assistant') return false;
  return todo.why !== null || todoWhyHref(todo, options) !== null;
}

/**
 * Where "Why?" leads for what the AI made: a `slack` to-do to its finding on the meeting's Slack tab — or, made by the
 * daily sweep and tied to no meeting (owner ruling Q5), to what it rests on on the Assistant's "Slack today" tab, for
 * a reader who can open it; an `assistant` to-do (owner ruling A6) to the finding it rests on when it rests on one,
 * and otherwise to the brief on the Assistant page. Null for the rest.
 */
export function todoWhyHref(todo: Pick<TodoDto, 'source' | 'meeting' | 'findingIds' | 'fromSweep'>, options: TodoWhyOptions = {}): string | null {
  return todoWhyLink(todo, options)?.href ?? null;
}

/** The "Why?" link with its words: what Slack shows, or the brief it came from. */
export function todoWhyLink(
  todo: Pick<TodoDto, 'source' | 'meeting' | 'findingIds' | 'fromSweep'>,
  options: TodoWhyOptions = {},
): { kind: 'slack' | 'brief'; href: string; label: string } | null {
  if (todo.source === 'slack' && todo.fromSweep) {
    return options.slackToday ? { kind: 'slack', href: slackTodayHref(todo.findingIds[0] ?? null), label: 'See what Slack shows' } : null;
  }
  if (todo.source === 'slack') {
    return todo.meeting === null ? null : { kind: 'slack', href: slackContextHref(todo.meeting.id, todo.findingIds), label: 'See what Slack shows' };
  }
  if (todo.source === 'assistant') {
    if (todo.meeting !== null && todo.findingIds.length > 0) return { kind: 'slack', href: slackContextHref(todo.meeting.id, todo.findingIds), label: 'See what Slack shows' };
    return { kind: 'brief', href: PATHS.assistant, label: 'Open the Assistant' };
  }
  return null;
}

/**
 * The action items the Slack tab lists under "What to do" (owner ruling A6): the meeting's `slack` to-dos
 * that are open or done — done ones stay, ticked, so the list shows what is left — in the order the
 * check made them. A dismissed one is set aside, as it is everywhere else.
 */
export function slackActionItemsToShow(actionItems: readonly TodoDto[]): TodoDto[] {
  return actionItems.filter((todo) => todo.status !== 'dismissed');
}

// --- "Since the last check" (owner ruling A13) ---------------------------------------------------------

/**
 * The verdicts that changed a conclusion, in the order the block lists them: what Slack settled first,
 * then what a later discussion overtook, then what no longer matters. Each carries evidence — a finding
 * of this result — so each line can lead to it.
 */
export const SLACK_CHANGED_VERDICT_ORDER: readonly SlackPriorVerdict[] = Object.freeze(['resolved', 'overtaken', 'no_longer_relevant']);

export interface SlackPriorReviewGroups {
  /** Resolved, then overtaken, then no longer relevant; within a verdict, in the order the check gave them. */
  changed: SlackPriorReviewDto[];
  /** What the check could not judge either way — listed plainly, with its reason. */
  unclear: SlackPriorReviewDto[];
  /** What the check found still true; collapsed under a count, since nothing about it changed. */
  stillHolds: SlackPriorReviewDto[];
}

/** How the block reads the verdicts: what changed, what is unclear, and what still holds. */
export function groupSlackPriorReview(entries: readonly SlackPriorReviewDto[]): SlackPriorReviewGroups {
  const ofVerdict = (verdict: SlackPriorVerdict) => entries.filter((entry) => entry.verdict === verdict);
  return {
    changed: SLACK_CHANGED_VERDICT_ORDER.flatMap(ofVerdict),
    unclear: ofVerdict('unclear'),
    stillHolds: ofVerdict('still_holds'),
  };
}

/** The verdict's word, from the one place it is declared. */
export function slackPriorVerdictLabel(verdict: SlackPriorVerdict): string {
  return SLACK_PRIOR_VERDICT_LABELS[verdict];
}

/**
 * What kind of conclusion was judged, named as the tab names it elsewhere: a finding by its kind (an
 * opportunity by its type too), or the to-do or note the earlier check made.
 */
export function slackPriorSubjectKind(prior: SlackPriorReviewSubjectDto): string {
  if (prior.type === 'todo') return 'To-do';
  if (prior.type === 'note') return 'Note';
  if (prior.kind === 'opportunity' && prior.opportunityType !== null) return `${SLACK_FINDING_KIND_NAMES.opportunity} · ${SLACK_OPPORTUNITY_TYPE_LABELS[prior.opportunityType]}`;
  return SLACK_FINDING_KIND_NAMES[prior.kind];
}

/** The conclusion's own words as they were when judged: a finding's title, a to-do's text, a note's sentence. */
export function slackPriorSubjectText(prior: SlackPriorReviewSubjectDto): string {
  return prior.type === 'finding' ? prior.title : prior.text;
}

/**
 * Where a verdict's evidence is: this result's first finding it cites, on this tab — or null for a
 * verdict that cites none (a still-holds or unclear verdict need not; a changed one always does).
 */
export function slackPriorEvidenceHref(meetingId: string, entry: Pick<SlackPriorReviewDto, 'evidenceFindingIds'>): string | null {
  return entry.evidenceFindingIds.length > 0 ? slackContextHref(meetingId, entry.evidenceFindingIds) : null;
}

/** "compared with the check of 27 Sep 2026, 14:10 BST", for the provenance line; null when no earlier check was judged. */
export function slackComparedWithText(comparedWith: { completedAt: string } | null, timeZone: string): string | null {
  return comparedWith ? `compared with the check of ${formatDateTime(comparedWith.completedAt, { timeZone })}` : null;
}

/**
 * The words on the still-holds disclosure. "Conclusions", not "findings": what the check re-confirmed
 * may be a to-do or a note it made as well as a finding, and the list beneath names each by its kind.
 */
export function slackStillHoldsText(count: number): string {
  return pluralise(count, { one: '# earlier conclusion still holds', other: '# earlier conclusions still hold' });
}

/** The count beside the block's heading: how many conclusions changed. */
export function slackReviewChangedText(count: number): string {
  return count === 0 ? 'Nothing changed' : pluralise(count, { one: '# changed', other: '# changed' });
}

// --- The "Look in Slack" dialog ------------------------------------------------------------------------

const HOUR_MS = 60 * 60_000;
const DAY_MS = 24 * HOUR_MS;

/**
 * "13 – 23 Sep 2026 (3 days before the meeting to 7 days after it)", or "… to now" while that end is
 * still to come — the window is never read past the moment of the check. Mirrors the server's window
 * (a meeting without an end is taken to last an hour).
 */
export function slackWindowText(window: { start: string; end: string } | null, meeting: Pick<MeetingDetail, 'startedAt' | 'endedAt'>, timeZone: string): string | null {
  if (window === null) return null;
  const range = formatDateRange(window.start, window.end, timeZone);
  const start = meeting.startedAt ? Date.parse(meeting.startedAt) : Number.NaN;
  const ended = meeting.endedAt ? Date.parse(meeting.endedAt) : Number.NaN;
  const end = !Number.isNaN(ended) && !Number.isNaN(start) && ended >= start ? ended : start + HOUR_MS;
  const clamped = !Number.isNaN(end) && Date.parse(window.end) < end + SLACK_WINDOW_DAYS_AFTER * DAY_MS;
  return `${range} (${SLACK_WINDOW_DAYS_BEFORE} days before the meeting to ${clamped ? 'now' : `${SLACK_WINDOW_DAYS_AFTER} days after it`})`;
}

/**
 * What "Look in Slack" may read, as the dialog says it (owner ruling R7): everything the person can see
 * in Slack, less what they left out; direct messages and group chats named only when this run may
 * include them.
 */
export function slackDialogScope(preview: Pick<SlackContextPreviewResponse, 'readableKinds' | 'searchGranted' | 'directMessages'>): string {
  const scope = slackScopePhrase(preview.readableKinds, { search: preview.searchGranted, includeDms: preview.directMessages.available });
  if (scope === null) return 'Nothing: your Slack grant does not let Calis read any kind of conversation.';
  return `Everything you can see in Slack: ${scope}.`;
}

/** The channels the person left out, as the dialog counts them; where they change the list. */
export function slackExcludedText(excluded: number): string {
  if (excluded <= 0) return 'None.';
  return pluralise(excluded, {
    one: '# channel you told Calis not to read: never read, kept or sent, and nothing a search finds in it is used.',
    other: '# channels you told Calis not to read: never read, kept or sent, and nothing a search finds in them is used.',
  });
}

/**
 * What happens when the person presses "Look in Slack" (R8): how a check looks — judged searches first,
 * every conversation last — where what it reads goes, the judges' calls included, and what is kept.
 */
export function slackCheckExplainer(input: { ai: AiProvider; retentionDays: number; climate?: boolean }): string {
  // Why "apart from" while executive intelligence is on (ADR 0019; design §2.14 item 7): the check then also answers one
  // closed word for the climate of what it read, counted into the organisation pulse — the one thing of it not the
  // person's alone, said where they decide to look.
  const kept = `Calis keeps its findings and short quoted excerpts, encrypted, for ${pluralise(input.retentionDays, { one: '# day', other: '# days' })}, and how it looked only as counts; ${input.climate === true ? ONLY_YOU_BUT_CLIMATE : 'only you can see them'}.`;
  const steps = `${slackCheckSteps(input.ai)}. The ${SLACK_LOOKBACK_DAYS} days before the window are read only to find threads still going on in it.`;
  if (input.ai === 'offline') {
    return `${steps} What the searches found, the messages related to this meeting — ${SLACK_CHECK_SENDS.thread} — and the meeting’s record and transcript go to the demo judge and analyser, which run on this server and send nothing outside it. ${kept}`;
  }
  const to = input.ai === 'anthropic' ? 'Anthropic’s Claude' : 'the AI provider';
  // Why the whole list (ADR 0009 §3; ADR 0010): the person decides here, so they are told everything that goes.
  return `${steps} After each search, ${SLACK_CHECK_SENDS.found}, go to ${to} with ${SLACK_CHECK_SENDS.judge}, for the judgement. Then the messages related to this meeting — ${SLACK_CHECK_SENDS.thread} — go to ${to} to compare, with their authors’ names and where they were posted, together with ${SLACK_CHECK_SENDS.meeting}. ${capitalise(SLACK_CHECK_SENDS.never)}. ${kept}`;
}

function capitalise(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/**
 * The one choice the dialog offers for this run (owner ruling R7): direct messages and group chats —
 * all of them, unless the person switched them off — which this run may leave out. Why the attendees
 * are counted, not found: the preview calls no Slack method, so which attendees are in Slack is known
 * only when the check runs, and the result says how many it found. Their DMs are read first, so a budget
 * that runs out cuts the others.
 */
export function slackDirectMessageOption(attendees: number, kinds: readonly SlackConversationKind[] = ['im', 'mpim']): { label: string; description: string } {
  const words = directMessageWords(kinds) ?? 'direct messages and group chats';
  const first =
    attendees > 0
      ? pluralise(attendees, {
        one: ' Those with the attendee who has an email address are read first, if Calis finds them in Slack.',
        other: ' Those with the # attendees who have an email address are read first, if Calis finds them in Slack.',
      })
      : '';
  return {
    label: `Include ${words} in this check`,
    description: `All of them, those with people outside Centro too. Untick to leave them out of this check only.${first}`,
  };
}

/**
 * Why "Look in Slack" cannot be pressed in the dialog; null when it can. Why the search counts: it reaches
 * every public channel the person never joined (owner ruling R7), whatever their own list holds, so only
 * with no search, no channel of theirs and no direct messages is there nothing to read.
 */
export function slackDialogBlockedReason(input: { channels: number; searchReach: boolean; includeDms: boolean; runsLeftToday: number; runsPerDay: number }): string | null {
  if (input.runsLeftToday <= 0) return slackDailyLimitText(input.runsPerDay);
  if (input.channels === 0 && !input.searchReach && !input.includeDms) {
    return 'No channel in your list is one Calis may read, and Slack’s search is not available to it, so this check needs your direct messages and group chats. Tick them, or change what Calis may read in Connections.';
  }
  return null;
}

/**
 * What the tab says when a check would have nothing to read (state `no_sources`), from the facts the
 * server found: only what is true of this person's list, switch and installation, and only the changes
 * that would help.
 */
export function slackNothingToReadText(facts: SlackNothingToReadDto | null): string {
  if (facts === null) return 'Calis has no channel, direct message or search it may use in Slack for this check. See what it may read in Connections.';
  return NOTHING_TO_READ_TEXT[facts.channels][facts.directMessages];
}

/**
 * The four cases of `slackNothingToReadText`, each written out whole. Why (05-content §8; the Phase 1c audit's D30): the
 * text was two clauses chosen apart, the remedies joined with ", or " and capitalised, and a comma spliced in by count.
 */
const NOTHING_TO_READ_TEXT: Readonly<Record<SlackNothingToReadDto['channels'], Readonly<Record<SlackNothingToReadDto['directMessages'], string>>>> = {
  none_listed: {
    switched_off: 'Your channel list holds no channel Calis may read (it may not have loaded yet), your direct messages and group chats are switched off, and Slack’s search is not available to Calis. Refresh your channel list, or switch direct messages and group chats back on, in Connections.',
    not_read: 'Your channel list holds no channel Calis may read (it may not have loaded yet), Calis does not read direct messages or group chats here, and Slack’s search is not available to Calis. Refresh your channel list in Connections.',
  },
  all_left_out: {
    switched_off: 'Every channel in your list is one Calis won’t read, your direct messages and group chats are switched off, and Slack’s search is not available to Calis. Remove a channel from that list, or switch direct messages and group chats back on, in Connections.',
    not_read: 'Every channel in your list is one Calis won’t read, Calis does not read direct messages or group chats here, and Slack’s search is not available to Calis. Remove a channel from that list in Connections.',
  },
};

/** "47 of 50 Slack checks left today." */
export function slackRunsLeftText(runsLeftToday: number, runsPerDay: number): string {
  return `${formatCount(Math.max(0, runsLeftToday))} of ${formatCount(runsPerDay)} Slack checks left today.`;
}
